import { FileUp } from "lucide-react";
import { type DragEvent, useRef, useState } from "react";

import { Motion, Presence } from "./presence";

/** Profondeur des survols imbriqués d'un glisser-déposer : chaque entrée dans un élément enfant compte, chaque
 *  sortie décompte ; la zone ne s'éteint qu'en revenant à zéro (sinon elle clignoterait d'un enfant à l'autre). */
export function nextDragDepth(depth: number, event: "enter" | "leave" | "drop"): number {
  if (event === "drop") return 0;
  return event === "enter" ? depth + 1 : Math.max(0, depth - 1);
}

/** Dépôt d'un fichier sur toute une vue (ordres reçus, RETEX) : à poser sur la racine de la vue (`handlers`) ;
 *  `dragging` affiche la `DropZone`. `enabled` : la vue accepte un fichier (dossier ouvert, pas d'import en cours) ;
 *  `onFile` reçoit le premier fichier déposé. Seuls les glisser de fichiers comptent (pas de texte ni de lien). */
export function useFileDrop(enabled: boolean, onFile: (file: File) => void) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");
  const handlers = {
    onDragEnter: (e: DragEvent) => {
      if (!enabled || !hasFiles(e)) return;
      depth.current = nextDragDepth(depth.current, "enter");
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (enabled && hasFiles(e)) e.preventDefault();
    },
    onDragLeave: () => {
      depth.current = nextDragDepth(depth.current, "leave");
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      depth.current = nextDragDepth(depth.current, "drop");
      setDragging(false);
      if (!enabled) return;
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) onFile(file);
    },
  };
  return { dragging, handlers };
}

/** Zone de dépôt d'un fichier (R11, R3) : cadre en pointillés `accent` sur fond blanc translucide, icône de 24 px et
 *  consigne en 13 px, qui apparaît et disparaît en fondu. À poser dans le conteneur `relative` du contenu de la
 *  vue ; elle laisse passer le pointeur (le dépôt est reçu par la vue, `useFileDrop`). */
export function DropZone({ show, label }: { show: boolean; label: string }) {
  return (
    <Presence show={show}>
      {show && (
        <Motion
          origin="fade"
          className="pointer-events-none absolute inset-3 z-30 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-accent bg-surface/85 px-6 text-center"
        >
          <FileUp size={24} className="text-accent" aria-hidden />
          <p className="text-title font-semibold text-ink">{label}</p>
        </Motion>
      )}
    </Presence>
  );
}
